import React, { useEffect } from "react";
import { Constants } from "@videosdk.live/react-sdk";
import { emojis } from "../../utils/list";
import { toast } from "react-toastify";

const MobileSwipeUpPanel = ({
  showMobilePanel,
  isMobileLike,
  isMobileLandscape,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  setShowMobilePanel,
  setShowCaptionsDropdown,
  showCaptionsDropdown,
  setShowReactions,
  showReactions,
  setShowToolsDropdown,
  setShowLayoutDropdown,
  showLayoutDropdown,
  meetingRole,
  mMeeting,
  handleRecordingClick,
  meetingSettings,
  publishRaiseHand,
  showMore,
  setShowMore,
  setShowHelpModal,
  translationEnabled,
  setTranslationEnabled,
  stopTranslation,
  startTranslation,
  selectedLanguage,
  changeTranslationLanguage,
  setSelectedLanguage,
  translationStatus,
  showElementSection,
  setShowElementSection,
  setShowPollsModal,
  handleEmojiClick,
  // livestream props
  setShowLiveStreamModal,
  isLiveStreaming,
  handleStopLiveStream,
  setShowCaptionTranslationPanel,
  // layout props
  selectedLayout,
  setSelectedLayout,
  hideSelfView,
  setHideSelfView,
  showNonVideoParticipants,
  setShowNonVideoParticipants,
  handleToggleFullscreen,
  isFullscreen,
}) => {
  const landscapePanelWidth = 340; // px
  useEffect(() => {
    const detail = {
      open: !!(showMobilePanel && isMobileLandscape),
      width: landscapePanelWidth,
    };
    window.dispatchEvent(new CustomEvent("rlink:mobile-panel", { detail }));
    return () => {
      window.dispatchEvent(
        new CustomEvent("rlink:mobile-panel", {
          detail: { open: false, width: landscapePanelWidth },
        })
      );
    };
  }, [showMobilePanel, isMobileLandscape]);

  if (!showMobilePanel || !isMobileLike) return null;

  return (
    <div
      className={`fixed z-[9999] ${
        isMobileLandscape
          ? "right-0 top-16 bottom-0 pointer-events-none"
          : "inset-0 bg-black/50 backdrop-blur-sm"
      }`}
      onClick={(e) => {
        if (!isMobileLandscape) {
          setShowMobilePanel(false);
          setShowCaptionsDropdown(false);
          setShowReactions(false);
          setShowLayoutDropdown(false);
        }
      }}
    >
      <div
        className={`absolute bg-[#060621] overflow-y-auto ${
          isMobileLandscape
            ? "right-20 top-0 bottom-0 w-[340px] rounded-l-2xl p-4 shadow-xl border-l border-slate-700 pointer-events-auto"
            : "bottom-0 left-0 right-0 rounded-t-2xl p-6 max-h-[70vh]"
        }`}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {/* Swipe Indicator */}
        {!isMobileLandscape && (
          <div
            className={`flex justify-center cursor-pointer ${
              isMobileLandscape ? "my-2" : "mb-6 py-2 -mt-2"
            }`}
            onClick={(e) => {
              e.stopPropagation();
              setShowMobilePanel(false);
              setShowCaptionsDropdown(false);
              setShowReactions(false);
              setShowLayoutDropdown(false);
            }}
          >
            <div
              className={`bg-gray-500 rounded-full ${
                isMobileLandscape ? "w-1 h-10" : "w-12 h-1"
              }`}
            ></div>
          </div>
        )}

        {/* Panel Content - Grid Layout */}
        <div className="space-y-6">
          <div
            className={`grid gap-4 ${
              isMobileLandscape ? "grid-cols-3" : "grid-cols-5"
            }`}
          >
            {/* Record */}
            {["moderator", "co-host"].includes(meetingRole) && (
              <button
                onClick={() => {
                  handleRecordingClick();
                  setShowMobilePanel(false);
                }}
                className={`flex flex-col items-center justify-center ${
                  isMobileLandscape ? "p-0 gap-0 " : "p-3 gap-2 "
                } hover:opacity-80 transition-opacity`}
              >
                <img
                  src={
                    mMeeting.recordingState ==
                    Constants.recordingEvents.RECORDING_STOPPED
                      ? "/icons/recordOff.svg"
                      : "/icons/recordOn.svg"
                  }
                  alt="Record"
                  className="w-5 h-5"
                />
                <span className="text-[#E0E0E0] text-xs text-center">
                  Record
                </span>
              </button>
            )}

            {/* Raise Hand */}
            {meetingSettings && meetingSettings.allowRaiseHand && (
              <button
                onClick={() => {
                  publishRaiseHand("Raise Hand");
                  toast("Raised hand", {
                    position: "bottom-left",
                    autoClose: 3000,
                    hideProgressBar: true,
                    closeButton: false,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                  });
                  setShowMobilePanel(false);
                }}
                className="flex flex-col items-center justify-center gap-2 p-3 hover:opacity-80 transition-opacity"
              >
                <img
                  src="/icons/raiseHand.svg"
                  alt="Raise Hand"
                  className="w-5 h-5"
                />
                <span className="text-[#E0E0E0] text-xs text-center">Hand</span>
              </button>
            )}

            {/* Settings */}
            <button
              onClick={() => {
                setShowMore(!showMore);
                setShowMobilePanel(false);
              }}
              className="flex flex-col items-center justify-center gap-2 p-3 hover:opacity-80 transition-opacity"
            >
              <img
                src="/icons/settings.svg"
                alt="Settings"
                className="w-5 h-5"
              />
              <span className="text-[#E0E0E0] text-xs text-center">
                Settings
              </span>
            </button>

            {/* Live Stream */}
            {["moderator", "co-host"].includes(meetingRole) && (
              <button
                onClick={() => {
                  if (isLiveStreaming) {
                    handleStopLiveStream();
                  } else {
                    setShowLiveStreamModal(true);
                  }
                  setShowMobilePanel(false);
                  setShowCaptionsDropdown(false);
                  setShowReactions(false);
                  setShowToolsDropdown(false);
                  setShowLayoutDropdown(false);
                }}
                className="flex flex-col items-center justify-center gap-2 p-3 hover:opacity-80 transition-opacity"
              >
                <img
                  src={
                    isLiveStreaming
                      ? "/icons/livestreamOn.svg"
                      : "/icons/livestream.svg"
                  }
                  alt="Live Stream"
                  className="w-5 h-5"
                />
                <span className="text-[#E0E0E0] text-xs text-center">
                  {isLiveStreaming ? "Stop Stream" : "Live Stream"}
                </span>
              </button>
            )}

            {/* Help */}
            <button
              onClick={() => {
                setShowHelpModal(true);
                setShowMobilePanel(false);
              }}
              className="flex flex-col items-center justify-center gap-2 p-3 hover:opacity-80 transition-opacity"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5 text-[#E0E0E0]"
              >
                <circle cx="12" cy="12" r="9" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12" y2="17" />
              </svg>
              <span className="text-[#E0E0E0] text-xs text-center">
                Feedback
              </span>
            </button>

            {/* Captions */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowMobilePanel(false);
                setShowCaptionsDropdown(false);
                setShowToolsDropdown(false);
                setShowLayoutDropdown(false);
                setShowReactions(false);
                if (setShowCaptionTranslationPanel) {
                  setShowCaptionTranslationPanel(true);
                }
              }}
              className="flex flex-col items-center justify-center gap-2 p-3 hover:opacity-80 transition-opacity"
            >
              <img
                src="/icons/aiCaption.svg"
                alt="Captions"
                className="w-5 h-5"
              />
              <span className="text-[#E0E0E0] text-xs text-center">
                Captions
              </span>
            </button>

            {/* Elements */}
            {["moderator", "co-host"].includes(meetingRole) && (
              <button
                onClick={() => {
                  setShowElementSection(!showElementSection);
                  setShowMobilePanel(false);
                }}
                className="flex flex-col items-center justify-center gap-2 p-3 hover:opacity-80 transition-opacity"
              >
                <img
                  src="/icons/elements.svg"
                  alt="Elements"
                  className="w-5 h-5"
                />
                <span className="text-[#E0E0E0] text-xs text-center">
                  Elements
                </span>
              </button>
            )}


            {/* Layout */}
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowLayoutDropdown(!showLayoutDropdown);
                  setShowCaptionsDropdown(false);
                  setShowToolsDropdown(false);
                  setShowReactions(false);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                className="flex flex-col items-center justify-center gap-2 p-3 hover:opacity-80 transition-opacity w-full"
              >
                <img
                  src="/icons/layout.svg"
                  alt="Layout"
                  className="w-5 h-5"
                />
                <span className="text-[#E0E0E0] text-xs text-center">
                  Layout
                </span>
              </button>
              {/* Layout Dropdown */}
              {showLayoutDropdown && (
                <div
                  className={`fixed bg-[#060621] rounded-lg shadow-xl border border-gray-600 z-[100] py-3 px-4 transition-all duration-300 ease-in-out ${
                    isMobileLandscape
                      ? "right-20 top-0 bottom-0 w-[340px]"
                      : "inset-x-4 bottom-24 max-h-[50vh] overflow-y-auto"
                  }`}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  {/* VIEW Section */}
                  <div className="px-2 py-2">
                    <div className="text-white text-center text-sm tracking-widest font-medium mb-4">
                      VIEW
                    </div>

                    {/* Layout Options */}
                    <div className="grid grid-cols-2 gap-3">
                      {/* Gallery Layout */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedLayout("gallery");
                          setShowLayoutDropdown(false);
                          setShowMobilePanel(false);
                        }}
                        className={`p-3 rounded-lg border transition-colors ${
                          selectedLayout === "gallery"
                            ? "border-[#7700ff] bg-[#7700ff]/10"
                            : "border-gray-600 hover:border-gray-500"
                        }`}
                      >
                        <div className="flex flex-col items-center gap-2">
                          <div className="">
                            <img
                              src="/icons/gallery.svg"
                              alt="gallery"
                              className="w-full h-full object-contain"
                            />
                          </div>
                          <span className="text-white text-xs">Gallery</span>
                        </div>
                      </button>

                      {/* Speaker Top Layout */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedLayout("speaker-top");
                          setShowLayoutDropdown(false);
                          setShowMobilePanel(false);
                        }}
                        className={`p-3 rounded-lg border transition-colors ${
                          selectedLayout === "speaker-top"
                            ? "border-[#7700ff] bg-[#7700ff]/10"
                            : "border-gray-600 hover:border-gray-500"
                        }`}
                      >
                        <div className="flex flex-col items-center gap-2">
                          <div className="">
                            <img
                              src="/icons/speakerTop.svg"
                              alt="speakerTop"
                              className="w-full h-full object-contain"
                            />
                          </div>
                          <span className="text-white text-xs">
                            Speaker Top
                          </span>
                        </div>
                      </button>

                      {/* Focus Layout */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedLayout("focus");
                          setShowLayoutDropdown(false);
                          setShowMobilePanel(false);
                        }}
                        className={`p-3 rounded-lg border transition-colors ${
                          selectedLayout === "focus"
                            ? "border-[#7700ff] bg-[#7700ff]/10"
                            : "border-gray-600 hover:border-gray-500"
                        }`}
                      >
                        <div className="flex flex-col items-center gap-2">
                          <div className="">
                            <img
                              src="/icons/speakerFocus.svg"
                              alt="focus"
                              className="w-full h-full object-contain"
                            />
                          </div>
                          <span className="text-white text-xs">Focus</span>
                        </div>
                      </button>

                      {/* Speaker Side Layout */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedLayout("speaker-side");
                          setShowLayoutDropdown(false);
                          setShowMobilePanel(false);
                        }}
                        className={`p-3 rounded-lg border transition-colors ${
                          selectedLayout === "speaker-side"
                            ? "border-[#7700ff] bg-[#7700ff]/10"
                            : "border-gray-600 hover:border-gray-500"
                        }`}
                      >
                        <div className="flex flex-col items-center gap-2">
                          <div className="">
                            <img
                              src="/icons/speakerSide.svg"
                              alt="speakerSide"
                              className="w-full h-full object-contain"
                            />
                          </div>
                          <span className="text-white text-xs">
                            Speaker Side
                          </span>
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Separator */}
                  <div className="border-t border-gray-600 my-4"></div>

                  {/* OPTIONS Section */}
                  <div className="px-2 py-2">
                    <div className="text-white text-center text-sm tracking-widest font-medium mb-3">
                      OPTIONS
                    </div>

                    <div className="space-y-2">
                      {/* Fullscreen Option */}
                      {handleToggleFullscreen && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowLayoutDropdown(false);
                            handleToggleFullscreen();
                            setShowMobilePanel(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-sm ${
                            !isFullscreen
                              ? "text-gray-400 hover:text-white"
                              : "text-white hover:bg-white/10 transition-colors"
                          }`}
                        >
                          Fullscreen
                        </button>
                      )}

                      {/* Hide self view Option */}
                      {mMeeting &&
                        mMeeting.participants &&
                        Array.from(mMeeting.participants.keys()).length > 1 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setHideSelfView(!hideSelfView);
                              setShowLayoutDropdown(false);
                              setShowMobilePanel(false);
                            }}
                            className={`w-full text-left px-3 py-2 text-sm ${
                              hideSelfView ? "text-white" : "text-gray-400"
                            }`}
                          >
                            Hide self view
                          </button>
                        )}

                      {/* Show non-video participants Option */}
                      {selectedLayout == "gallery" && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowNonVideoParticipants(
                              !showNonVideoParticipants
                            );
                            setShowLayoutDropdown(false);
                            setShowMobilePanel(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-sm ${
                            showNonVideoParticipants
                              ? "text-white"
                              : "text-gray-400"
                          }`}
                        >
                          Show non-video participants
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Reactions */}
            {meetingSettings && meetingSettings.allowReactions && (
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowReactions(!showReactions);
                    setShowCaptionsDropdown(false);
                    setShowToolsDropdown(false);
                    setShowLayoutDropdown(false);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="flex flex-col items-center justify-center gap-2 p-3 hover:opacity-80 transition-opacity w-full"
                >
                  <img
                    src="/icons/reaction.svg"
                    alt="Reactions"
                    className="w-5 h-5"
                  />
                  <span className="text-[#E0E0E0] text-xs text-center">
                    Reactions
                  </span>
                </button>
                {/* Reactions Dropdown */}
                {showReactions && (
                  <div
                    className="fixed inset-x-4 bottom-24 bg-[#1a1a2e] border border-gray-600 rounded-lg p-4 z-[100] max-h-[40vh] overflow-y-auto"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <div className="grid grid-cols-5 gap-3">
                      {emojis.map(({ char, alt }) => (
                        <button
                          key={char}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEmojiClick({ char, alt });
                            setShowReactions(false);
                            setShowMobilePanel(false);
                          }}
                          className="w-12 h-12 text-2xl rounded-lg bg-gray-700 hover:bg-gray-600 flex items-center justify-center transition-colors"
                          title={alt}
                        >
                          {char}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileSwipeUpPanel;
